import { ourLeaders } from "@/data/aboutUs";
import LucideIcon from "@/utils/LucideIcon";
import { motion } from "framer-motion";
import { useState } from "react";
import { FaLinkedin } from "react-icons/fa";

const LeaderCard = ({ name, designation, message, socials, image }: any) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative flex flex-col lg:flex-row items-center bg-white rounded-3xl p-4 lg:p-6 hover:shadow-xl h-full group overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image */}
      <div className="relative w-full sm:w-72 h-64 sm:h-80 rounded-3xl overflow-hidden flex-shrink-0">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-contain lg:object-cover transition-transform duration-500 group-hover:scale-105"
        />

        {/* Overlay */}
        {/* <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" /> */}
      </div>

      {/* Content */}
      <div className="mt-6 lg:mt-0 lg:ml-10 flex-1 relative z-10 text-center lg:text-left">
        <h2
          className="text-xl font-bold text-gray-900 mb-3 transition-colors duration-300 group-hover:text-blue-700"
          style={{
            transform: isHovered ? "translateX(4px)" : "translateX(0px)",
            transition: "transform 0.3s ease, color 0.3s ease",
          }}
        >
          {name}
        </h2>

        <p
          className="text-base font-medium text-blue-600 mb-4"
          style={{
            transform: isHovered ? "translateX(4px)" : "translateX(0px)",
            transition: "transform 0.3s ease 0.1s, color 0.3s ease",
          }}
        >
          {designation}
        </p>

        <div
          className="h-px bg-gradient-to-r from-gray-300 to-transparent mb-6 transition-all duration-500 mx-auto lg:mx-0"
          style={{
            width: isHovered ? "100%" : "80%",
            background: isHovered
              ? "linear-gradient(to right, rgb(59, 130, 246), rgb(147, 51, 234), transparent)"
              : "linear-gradient(to right, gray, transparent)",
          }}
        />

        <p className="text-gray-500 text-sm leading-relaxed mb-8 group-hover:text-gray-800 transition-colors duration-300">
          {message}
        </p>

        <div className="flex justify-center lg:justify-start space-x-4">
          {socials.linkedin && (
            <a
              target="_blank"
              href={socials.linkedin}
              className="relative p-3 rounded-full bg-blue-50 text-gray-500 group-hover:text-blue-600 hover:scale-110"
              style={{
                transform: isHovered ? "translateY(-2px)" : "translateY(0px)",
                transition:
                  "transform 0.3s ease 0.3s, background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease",
              }}
            >
              <FaLinkedin
                size={28}
                className="transition-transform duration-300 group-hover/link:scale-110"
              />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const TeamCard = ({ member, index }: any) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5 }}
      className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden w-60 group"
    >
      <div className="p-4">
        <div className="w-full h-48 aspect-square mb-4 rounded-xl overflow-hidden bg-gray-100 relative group">
          <img
            src={member.image}
            alt={member.name}
            className="w-full h-full object-cover"
          />

          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-blue-800 group-hover:w-full transition-all duration-500 ease-out rounded-full"></div>

          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-blue-800 group-hover:w-full transition-all duration-500 ease-out rounded-full"></div>
        </div>

        <div className="text-left">
          <h3 className="text-base font-semibold text-gray-900 mb-1">
            {member.name}
          </h3>
          <p className="text-sm text-gray-500 mb-3 group-hover:text-blue-600 transition-colors duration-300">
            {member.position}
          </p>

          <div
            className="h-px bg-gradient-to-r from-gray-300 to-transparent mb-6 transition-all duration-500 mx-auto lg:mx-0"
            style={{
              width: "100%",
            }}
          />

          <div className="flex justify-start">
            <motion.a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 rounded-full bg-blue-50 text-gray-500 hover:text-blue-600 transition-colors duration-200"
            >
              <FaLinkedin size={28} />
            </motion.a>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Main Section Component
const SectionOurTeam = () => {
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut" as const,
      },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  return (
    <div className="w-full py-20 px-4 text-center md:text-left">
      <motion.div
        className="text-center md:text-left mb-20"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
      >
        <motion.h2
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="flex items-center md:text-left justify-center md:justify-start mb-4">
            <LucideIcon
              name="BadgeCheck"
              className="w-10 h-10 text-blue-900 hidden md:inline-block mr-3 hover:text-blue-900"
            />
            <h2 className="text-4xl md:text-5xl font-bold font-inter text-secondary-gradient bg-clip-text text-transparent">
              Meet Our Visionaries
            </h2>
          </div>
        </motion.h2>
        <motion.div
          className="w-24 h-1 bg-secondary-gradient mx-auto md:mx-0 md:ml-14 mb-8 rounded-xl"
          initial={{ width: 0 }}
          whileInView={{ width: 96 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ amount: 0.2 }}
        ></motion.div>

        <motion.p
          className="text-xl text-gray-600 max-w-3xl leading-relaxed font-inter"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          Creating impact through vision, strategy, and meaningful action.
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {ourLeaders.map((member, index) => (
          <LeaderCard
            key={index}
            name={member.name}
            designation={member.designation}
            message={member.message}
            socials={member.socials}
            image={member.image}
          />
        ))}
      </div>

      {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-20 p-2">
        {ourTeam.map((member, index) => (
          <TeamCard key={member.id} member={member} index={index} />
        ))}
      </div> */}
    </div>
  );
};

export default SectionOurTeam;
